import { PublicProvider } from '../../../../api/Providers';
import Currency from '../../../../modules/Currency';

import {
  getProAppointmentDetails,
  ProAppointmentDetails,
  DiscountCodeRedemption,
} from '../../../../api/Providers/Appointments';
import { getNCDDetails } from '../../../../modules/Api';
import { ApptsFromNotificationFeed } from '../../CelebrationModal/CelebrationModal.types';

type Service = {
  id: number;
  name: string;
  cost: string;
};

export type FormattedAppointment = {
  id: number;
  dayDate: string;
  dayDateTime: string;
  local_start: string;
  clientFirstName: string;
  clientLastName: string;
  clientThumb?: string;
  clientId: number;
  providerArea: string;
  estimatedAnnualRevenue: string;
  netPay: string;
  fee: string;
  feePercent: number;
  services: Service[];
  discount_code_redemption: DiscountCodeRedemption;
  hasNCDFeePaymentAttempt: boolean | null;
  rawAppointment: ProAppointmentDetails;
  isCancelled: boolean;
  isMonetizedNCD: boolean;
  ncdSource: string;
};

export const getFormattedAppointment = async (
  appointment: ApptsFromNotificationFeed,
  provider: PublicProvider,
): Promise<FormattedAppointment> => {
  const {
    appointment_id,
  } = appointment;
  let ncdSource = '';

  const rawAppointment = await getProAppointmentDetails(provider.id, Number(appointment_id));
  if (rawAppointment.is_monetized_new_client_delivery) {
    const ncdDetails = await getNCDDetails(provider.id, appointment_id);
    ncdSource = ncdDetails.source;
  }
  const { client } = rawAppointment;
  const providerArea = (provider.locations && provider.locations[0]?.city) || 'your area';
  const [
    clientFirstName,
    clientLastName,
  ] = client ? client.name.split(' ') : [
    '',
    '',
  ];

  const feePercent = (provider.ncd_enabled && Number(provider.ncd_fee_percent)) || 0;

  const appointmentDate = new Date(rawAppointment.local_start);
  const dateString = appointmentDate.toLocaleDateString(
    'en-US',
    {
      weekday: 'long',
      year: undefined,
      month: 'long',
      day: 'numeric',
    },
  );

  const timeString = appointmentDate.toLocaleTimeString(
    'en-US',
    {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
    },
  );

  const pretax = Number(rawAppointment.cost) + Number(rawAppointment.product_cost);

  const unformattedFee = rawAppointment.fees
    ? rawAppointment.fees.reduce(
      (previous, current) => (current.type === 'ncd_fee' ? previous + current.amount : previous),
      0,
    ) : 0;

  return {
    dayDate: dateString,
    dayDateTime: `${dateString} at ${timeString}`,
    local_start: rawAppointment.local_start,
    services: rawAppointment.time_blocks.map(({
      id,
      service_name: name,
      service_cost_cents: cost,
    }) => ({
      id,
      name,
      cost: Currency.format(cost / 100),
    })),
    clientLastName: `${clientLastName[0]}...`,
    clientFirstName,
    clientThumb: rawAppointment.client.thumbnail,
    providerArea,
    netPay: Currency.format(pretax - (unformattedFee / 100)),
    fee: Currency.format(unformattedFee / 100),
    id: Number(appointment_id),
    rawAppointment,
    feePercent,
    clientId: rawAppointment.client.id,
    estimatedAnnualRevenue: Currency.format(pretax * 4),
    discount_code_redemption: rawAppointment.discount_code_redemption,
    hasNCDFeePaymentAttempt: rawAppointment.has_ncd_fee_payment_attempt,
    isCancelled: rawAppointment.is_cancelled,
    isMonetizedNCD: rawAppointment.is_monetized_new_client_delivery,
    ncdSource,
  };
};
